import { apiFetch } from './client';

export interface Zone {
  id: string;
  name: string;
  sort_order: number;
  notes?: string | null;
}

export interface Location {
  id: string;
  name: string;
  color?: string | null;
  place_id?: string | null;
  formatted_address?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  notes?: string | null;
  zones: Zone[];
}

export interface ListLocationsResponse {
  locations: Location[];
  total: number;
}

export interface ListLocationsOptions {
  search?: string;
  limit?: number;
  offset?: number;
}

const orgPath = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/locations`;

export async function listLocations(
  token: string | null,
  slug: string,
  opts: ListLocationsOptions = {},
): Promise<ListLocationsResponse> {
  const qs = new URLSearchParams();
  if (opts.search) qs.set('search', opts.search);
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  const query = qs.toString();
  return apiFetch<ListLocationsResponse>(`${orgPath(slug)}${query ? `?${query}` : ''}`, { token });
}

export async function getLocation(
  token: string | null,
  slug: string,
  id: string,
): Promise<Location> {
  return apiFetch<Location>(`${orgPath(slug)}/${encodeURIComponent(id)}`, { token });
}

export interface CreateLocationInput {
  name: string;
  color?: string;
  place_id?: string;
  formatted_address?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  latitude?: number | null;
  longitude?: number | null;
  notes?: string;
  zones: { name: string; sort_order: number; notes?: string }[];
}

export async function createLocation(
  token: string | null,
  slug: string,
  input: CreateLocationInput,
): Promise<Location> {
  return apiFetch<Location>(orgPath(slug), { method: 'POST', body: input, token });
}

export interface UpdateLocationInput {
  name: string;
  color?: string;
  place_id?: string;
  formatted_address?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  latitude?: number | null;
  longitude?: number | null;
  notes?: string;
}

export async function updateLocation(
  token: string | null,
  slug: string,
  id: string,
  input: UpdateLocationInput,
): Promise<Location> {
  return apiFetch<Location>(`${orgPath(slug)}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    token,
  });
}

export async function deleteLocation(
  token: string | null,
  slug: string,
  id: string,
  reason = '',
): Promise<void> {
  await apiFetch<void>(`${orgPath(slug)}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: { reason },
    token,
  });
}

export interface ZoneInput {
  name: string;
  sort_order: number;
  notes?: string;
}

export async function addZone(
  token: string | null,
  slug: string,
  locationId: string,
  input: ZoneInput,
): Promise<Zone> {
  return apiFetch<Zone>(`${orgPath(slug)}/${encodeURIComponent(locationId)}/zones`, {
    method: 'POST',
    body: input,
    token,
  });
}

export async function updateZone(
  token: string | null,
  slug: string,
  locationId: string,
  zoneId: string,
  input: ZoneInput,
): Promise<Zone> {
  return apiFetch<Zone>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/zones/${encodeURIComponent(zoneId)}`,
    { method: 'PATCH', body: input, token },
  );
}

export async function deleteZone(
  token: string | null,
  slug: string,
  locationId: string,
  zoneId: string,
  reason = '',
): Promise<void> {
  await apiFetch<void>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/zones/${encodeURIComponent(zoneId)}`,
    { method: 'DELETE', body: { reason }, token },
  );
}

// Availability rules + exceptions. zone_id is null/omitted to target the
// whole location; otherwise it points to one location_zone. The semantics
// (zone overrides location; no rules = always available) live server-side
// and aren't re-derived here.

export interface AvailabilityRule {
  id: string;
  zone_id: string | null;
  day_of_week: number; // ISO 1..7
  start_time: string; // "HH:MM"
  end_time: string;
  effective_from: string | null; // "YYYY-MM-DD"
  effective_until: string | null;
  notes?: string | null;
}

export interface AvailabilityException {
  id: string;
  zone_id: string | null;
  on_date: string; // "YYYY-MM-DD"
  kind: 'closed' | 'hours';
  start_time: string | null;
  end_time: string | null;
  note?: string | null;
}

export interface AvailabilityResponse {
  location_id: string;
  // Keyed by zone id; "" key = location-level (zone_id IS NULL).
  rules: Record<string, AvailabilityRule[]>;
  exceptions: Record<string, AvailabilityException[]>;
}

export async function getAvailability(
  token: string | null,
  slug: string,
  locationId: string,
): Promise<AvailabilityResponse> {
  return apiFetch<AvailabilityResponse>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/availability`,
    { token },
  );
}

export interface SetAvailabilityRuleInput {
  day_of_week: number;
  start_time: string;
  end_time: string;
  effective_from?: string | null;
  effective_until?: string | null;
  notes?: string;
}

export async function setAvailabilityRules(
  token: string | null,
  slug: string,
  locationId: string,
  zoneId: string | null,
  rules: SetAvailabilityRuleInput[],
): Promise<{ rules: AvailabilityRule[] }> {
  return apiFetch<{ rules: AvailabilityRule[] }>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/availability/rules`,
    {
      method: 'PUT',
      body: { zone_id: zoneId, rules },
      token,
    },
  );
}

export interface ExceptionInput {
  zone_id?: string | null;
  on_date: string;
  kind: 'closed' | 'hours';
  start_time?: string | null;
  end_time?: string | null;
  note?: string;
}

export async function addAvailabilityException(
  token: string | null,
  slug: string,
  locationId: string,
  input: ExceptionInput,
): Promise<AvailabilityException> {
  return apiFetch<AvailabilityException>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/availability/exceptions`,
    { method: 'POST', body: input, token },
  );
}

export async function updateAvailabilityException(
  token: string | null,
  slug: string,
  locationId: string,
  exceptionId: string,
  input: ExceptionInput,
): Promise<AvailabilityException> {
  return apiFetch<AvailabilityException>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/availability/exceptions/${encodeURIComponent(exceptionId)}`,
    { method: 'PATCH', body: input, token },
  );
}

export async function deleteAvailabilityException(
  token: string | null,
  slug: string,
  locationId: string,
  exceptionId: string,
  reason = '',
): Promise<void> {
  await apiFetch<void>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/availability/exceptions/${encodeURIComponent(exceptionId)}`,
    { method: 'DELETE', body: { reason }, token },
  );
}

export type AvailabilityCheckReason =
  | 'no_rules'
  | 'available'
  | 'outside_recurring'
  | 'closed_by_exception';

export interface AvailabilityCheckResponse {
  available: boolean;
  reason: AvailabilityCheckReason;
  uncovered_intervals?: { start: string; end: string }[];
}

export async function checkAvailability(
  token: string | null,
  slug: string,
  locationId: string,
  input: { zone_id?: string | null; starts_at: string; ends_at: string },
): Promise<AvailabilityCheckResponse> {
  return apiFetch<AvailabilityCheckResponse>(
    `${orgPath(slug)}/${encodeURIComponent(locationId)}/availability/check`,
    { method: 'POST', body: input, token },
  );
}

// --- Bulk import (Excel parsed client-side, Places resolution per row,
//     then this JSON batch hits the server). Mirrors the members import shape
//     so the SPA can share its file-parsing pipeline.

export type LocationImportField = 'name' | 'address' | 'notes' | 'color';

export interface LocationMatchSuggestion {
  column: string;
  field?: LocationImportField | '';
  confidence: number;
}

export interface LocationRawSheet {
  sheet_name: string;
  rows: string[][];
}

export interface LocationSheetLayout {
  sheet_name: string;
  header_row_index: number;
  skip_rows_before_header: number;
  skip_row_pattern: string;
  notes?: string;
}

export interface LocationMatchColumnsResponse {
  layouts?: LocationSheetLayout[];
  suggestions: LocationMatchSuggestion[];
  source: 'ai' | 'fuzzy';
}

export async function matchLocationColumns(
  token: string | null,
  rawSheets: LocationRawSheet[],
): Promise<LocationMatchColumnsResponse> {
  return apiFetch<LocationMatchColumnsResponse>(
    `/api/v1/locations/import/match-columns`,
    { method: 'POST', body: { raw_sheets: rawSheets }, token },
  );
}

// LocationImportRow mirrors the Go struct (services/locations.ImportRow). The
// Address column is NOT sent — the SPA resolves it against Google Places
// before submission and ships the structured fields the server inserts.
// zones, when present, names the sub-areas the server creates under the
// location in the same transaction (matches the in-app split helper:
// 1/2 → ["A","B"], 1/4 → ["1A","1B","2A","2B"], 1/8 → 1A..2D).
export interface LocationImportRow {
  name: string;
  color?: string;
  notes?: string;
  place_id?: string;
  formatted_address?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  latitude?: number | null;
  longitude?: number | null;
  zones?: string[];
}

export interface LocationImportValidationError {
  row_index: number;
  field: string;
  reason: string;
}

export interface LocationImportSkipped {
  row_index: number;
  name: string;
  reason: string;
}

export interface LocationImportPreflight {
  validation_errors: LocationImportValidationError[];
}

export interface LocationBulkImportResponse {
  status: 'queued';
  row_count: number;
}

export async function validateLocationImport(
  token: string | null,
  rows: LocationImportRow[],
): Promise<LocationImportPreflight> {
  return apiFetch<LocationImportPreflight>(`/api/v1/locations/import/validate`, {
    method: 'POST',
    body: { rows },
    token,
  });
}

export async function bulkImportLocations(
  token: string | null,
  slug: string,
  rows: LocationImportRow[],
): Promise<LocationBulkImportResponse> {
  return apiFetch<LocationBulkImportResponse>(`${orgPath(slug)}/import`, {
    method: 'POST',
    body: { rows },
    token,
  });
}
